import {
  Button,
  Divider,
  Flex,
  Link,
  Skeleton,
  Text,
  Tooltip,
} from "@chakra-ui/react";
import { toZonedTime } from "date-fns-tz";
import { useRef } from "react";
import { RiShareBoxLine } from "react-icons/ri";
import { GenericError } from "src/components/Feedback/GenericError";
import { Card } from "src/components/Layout/Card";
import { ParentFormsLayoutInner } from "src/components/Layout/ParentFormsLayoutInner";
import { ParentFormsLayoutOuter } from "src/components/Layout/ParentFormsLayoutOuter";
import { RemoteDataView } from "src/components/Layout/RemoteDataView";
import { Glossary } from "src/components/Text/Glossary";
import * as FSR from "src/generated/hooks/form-service/requests";
import { useOrganization } from "src/hooks/useOrganization";
import { useQueryParams } from "src/hooks/useQueryParams";
import { useRemoteDataRestQuery } from "src/hooks/useRemoteDataQuery";
import { formatTimeWithTimeZone } from "src/scenes/orgAdmin/forms/export/dateUtils";
import { formatIsoDate, formatMoney } from "src/services/format";
import { isNonEmptyString, isNotNull } from "src/services/predicates";
import * as Url from "src/services/url";
import { PaidTag } from "./PaidTag";

const RECEIPT_POLL_INTERVAL_MS = 1_000;
const RECEIPT_MAX_POLL_DURATION_MS = 30_000;

export const Receipt = () => {
  const mountedAt = useRef(Date.now());
  const organization = useOrganization();
  const queryParams = useQueryParams();
  const sessionId = queryParams["session_id"];
  const paymentId = queryParams["paymentId"];

  const remoteData = useRemoteDataRestQuery({
    enabled: organization.hasData(),
    queryKey: ["GetPaymentReceipt", paymentId ?? sessionId],
    queryFn: () => {
      if (typeof paymentId === "string" && isNonEmptyString(paymentId)) {
        return FSR.getPaymentReceipt({ query: { paymentId } }).then(
          (resp) => resp.data
        );
      }

      if (typeof sessionId === "string" && isNonEmptyString(sessionId)) {
        return FSR.getPaymentReceipt({
          query: { sessionId, orgPath: organization.toNullable()?.path },
        }).then((resp) => resp.data);
      }

      throw new Error("Missing query string");
    },
    refetchOnMount: "always",
    refetchInterval: (query) => {
      if (Date.now() - mountedAt.current >= RECEIPT_MAX_POLL_DURATION_MS) {
        return false;
      }

      const receipt = query.state.data?.data;
      if (
        !receipt ||
        (receipt.paymentStatus === "paid" &&
          isNonEmptyString(receipt.receiptUrl))
      ) {
        return false;
      }

      return RECEIPT_POLL_INTERVAL_MS;
    },
  });

  return (
    <ParentFormsLayoutOuter
      heading="Receipt"
      backLink={{
        url: organization.map(Url.Parent.Payment.index).withDefault("#"),
        label: "Back to payemnts",
      }}
    >
      <ParentFormsLayoutInner>
        <RemoteDataView
          remoteData={remoteData}
          error={() => <GenericError />}
          loading={<Loading />}
          reloading={null}
        >
          {({ data }) => {
            const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
            const paidAt = data.paidAt ? toZonedTime(data.paidAt, tz) : null;
            const shortId = `${data.id.slice(0, 4)} ${data.id.slice(4, 8)}`;

            return (
              <Card showShadow useFlex gap="4" fontSize="sm">
                <Flex direction="column" alignItems="center" padding="2">
                  {data.paymentStatus === "paid" ? <PaidTag /> : null}
                  <Text fontWeight="semibold">{data.name}</Text>
                  <Text fontSize="2xl" fontWeight="semibold">
                    {formatMoney(data.amountInCents, data.currency)}
                  </Text>
                  {isNotNull(paidAt) && isNotNull(data.paidAt) ? (
                    <Text fontSize="xs">
                      {formatIsoDate(data.paidAt, "MMMM dd, yyyy", tz)} ·{" "}
                      {formatTimeWithTimeZone(paidAt, tz, true)}
                    </Text>
                  ) : null}
                </Flex>
                <Divider />
                <Flex direction="column" padding="2" gap="6">
                  <Flex direction="column" gap="2">
                    <Flex justifyContent="space-between">
                      <Text>Item</Text>
                      <Text>{data.name}</Text>
                    </Flex>
                    {isNotNull(data.form) ? (
                      <>
                        <Flex justifyContent="space-between">
                          <Text>Enrollment period</Text>
                          <Text>{data.form.enrollmentPeriodName}</Text>
                        </Flex>
                        <Flex justifyContent="space-between">
                          <Text>Form</Text>
                          <Text>{data.form.formTemplateName}</Text>
                        </Flex>
                        {isNonEmptyString(data.form.gradeName) ? (
                          <Flex justifyContent="space-between">
                            <Text>Grade</Text>
                            <Text>{data.form.gradeName}</Text>
                          </Flex>
                        ) : null}
                      </>
                    ) : null}
                    <Flex justifyContent="space-between">
                      <Glossary>Student</Glossary>
                      <Text>{data.person.fullName}</Text>
                    </Flex>
                    <Flex justifyContent="space-between">
                      <Text>Payment ID</Text>
                      <Tooltip label={data.id}>
                        <Text>{shortId}</Text>
                      </Tooltip>
                    </Flex>
                  </Flex>
                  <Flex direction="column" gap="2">
                    {isNotNull(data.receiptUrl) ? (
                      <Button
                        leftIcon={<RiShareBoxLine />}
                        variant="outline"
                        colorScheme="gray"
                        as={Link}
                        href={data.receiptUrl}
                        target="_blank"
                      >
                        View receipt
                      </Button>
                    ) : null}
                  </Flex>
                </Flex>
              </Card>
            );
          }}
        </RemoteDataView>
      </ParentFormsLayoutInner>
    </ParentFormsLayoutOuter>
  );
};

const Loading = () => {
  return (
    <Card showShadow gap="4" useFlex>
      <Flex direction="column" alignItems="center" padding="2" gap="2">
        <Skeleton width="100px" height="1rem" />
        <Skeleton width="150px" height="1rem" />
        <Skeleton width="150px" height="2rem" />
        <Skeleton width="170px" height="1rem" />
      </Flex>
      <Divider />
      <Flex direction="column" alignItems="center" padding="2" gap="2">
        <Skeleton width="100%" height="1rem" />
        <Skeleton width="100%" height="1rem" />
        <Skeleton width="100%" height="1rem" />
      </Flex>
    </Card>
  );
};
